import React, { useState, useEffect, useCallback } from 'react';
import HamburgerMenu from './HamburgerMenu';
import api from './api';
import Spinner from './Spinner';
import { useAuth } from './useAuth';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
        status: {
            UP: '#28a745',
            XC: '#20c997',
            DN: '#dc3545',
            DS: '#dc3545',
            PM: '#ffc107',
            FCF: '#8716bbff',
            AWP: '#335d80ff',
            OTH: '#6c757d',
            DET: '#6c757d',
            AIMD: '#6c757d',
            ACI: '#6c757d',
            REI: '#6c757d'
        }
    };

    return {
        page: { backgroundColor: colors.bg, minHeight: '100vh', padding: '20px' },
        container: { maxWidth: '1600px', margin: '0 auto', fontFamily: 'sans-serif' },
        pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
        pageTitle: { fontSize: '24px', margin: 0 },
        menuItem: {
            padding: '10px 15px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            display: 'block',
            textDecoration: 'none',
            color: colors.text,
        },
        menuItemButton: {
            background: 'none',
            border: 'none',
            padding: '10px 15px',
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '16px',
            fontFamily: 'sans-serif',
            color: colors.text,
        },
        card: { backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '20px', boxShadow: `0 2px 5px ${colors.shadow}`, marginBottom: '20px' },
        cardTitle: { fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', color: colors.text },
        tableContainer: { 
            backgroundColor: colors.cardBg, 
            borderRadius: '8px', 
            overflow: 'auto', 
            boxShadow: `0 2px 5px ${colors.shadow}`,
            maxHeight: 'calc(100vh - 300px)'
        },
        table: { width: '100%', borderCollapse: 'collapse' },
        tableHeader: { 
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa', 
            padding: '15px 10px', 
            borderBottom: `2px solid ${colors.border}`
        },
        tableHeaderCell: { 
            padding: '12px 8px', 
            textAlign: 'left', 
            fontWeight: 'bold', 
            color: colors.text, 
            cursor: 'pointer', 
            userSelect: 'none', 
            position: 'sticky',
            top: 0,
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa',
            zIndex: 10,
            fontSize: '12px' 
        },
        sortableHeader: { display: 'flex', alignItems: 'center', gap: '5px' },
        sortIcon: { fontSize: '10px', opacity: 0.7 },
        tableRow: { borderBottom: `1px solid ${colors.border}`, '&:hover': { backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa' } },
        tableCell: { padding: '8px', color: colors.text, verticalAlign: 'top', fontSize: '11px' },
        controlsContainer: { display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' },
        searchInput: { padding: '8px 12px', borderRadius: '4px', border: `1px solid ${colors.border}`, backgroundColor: isDark ? '#333' : '#fff', color: colors.text, minWidth: '200px' },
        filterSelect: { padding: '8px 12px', borderRadius: '4px', border: `1px solid ${colors.border}`, backgroundColor: isDark ? '#333' : '#fff', color: colors.text },
        priorityBar: { 
            height: '20px', 
            borderRadius: '10px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            color: 'white', 
            fontWeight: 'bold', 
            fontSize: '10px',
            minWidth: '60px'
        },
        rankCell: { 
            textAlign: 'center', 
            fontWeight: 'bold',
            padding: '8px 4px',
            borderRadius: '4px'
        },
        statusBadge: { padding: '3px 6px', borderRadius: '8px', color: 'white', fontWeight: 'bold', fontSize: '10px' },
        link: colors.link,
        status: colors.status,
        modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
        modal: { backgroundColor: colors.cardBg, padding: '20px', borderRadius: '8px', width: '500px', maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' },
        button: { padding: '10px 15px', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '10px', fontSize: '14px' },
    };
};

// Column Settings Modal Component
const ColumnSettingsModal = ({ columns, onClose, onSave, theme, isStaff }) => {
    const styles = getStyles(theme);
    const isDark = theme === 'dark';
    const [localColumns, setLocalColumns] = useState([...columns]);

    const handleToggleVisibility = (columnId) => {
        setLocalColumns(prev => prev.map(col => 
            col.id === columnId ? { ...col, visible: !col.visible } : col
        ));
    };

    const handleMove = (columnId, direction) => {
        setLocalColumns(prev => {
            const index = prev.findIndex(col => col.id === columnId);
            if (index === -1) return prev;
            if (direction === 'up' && index === 0) return prev;
            if (direction === 'down' && index === prev.length - 1) return prev;
            
            const newColumns = [...prev];
            const newIndex = direction === 'up' ? index - 1 : index + 1;
            [newColumns[index], newColumns[newIndex]] = [newColumns[newIndex], newColumns[index]];
            return newColumns;
        });
    };

    const handleReset = () => {
        const defaultColumns = getDefaultColumns(isStaff);
        setLocalColumns(defaultColumns);
    };

    const handleSave = () => {
        onSave(localColumns);
        onClose();
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <h2 style={{ marginTop: 0, color: styles.text }}>Customize Columns</h2>
                <div style={{ marginBottom: '20px', fontSize: '14px', color: styles.subtleText }}>
                    Check/uncheck to show/hide columns, use arrows to reorder
                </div>
                <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
                    {localColumns.map((col, index) => (
                        <div key={col.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px',
                            borderBottom: `1px solid ${styles.border}`,
                            backgroundColor: col.visible ? 'transparent' : styles.bg
                        }}>
                            <input
                                type="checkbox"
                                checked={col.visible}
                                onChange={() => handleToggleVisibility(col.id)}
                                style={{ cursor: 'pointer' }}
                                disabled={col.required}
                            />
                            <span style={{ flex: 1, color: col.visible ? styles.text : styles.subtleText }}>
                                {col.label}
                                {col.required && <span style={{ color: styles.subtleText, fontSize: '12px' }}> (required)</span>}
                            </span>
                            <button
                                onClick={() => handleMove(col.id, 'up')}
                                disabled={index === 0}
                                style={{
                                    ...styles.button,
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    opacity: index === 0 ? 0.5 : 1,
                                    cursor: index === 0 ? 'not-allowed' : 'pointer',
                                    backgroundColor: isDark ? '#444' : '#e0e0e0',
                                    color: styles.text
                                }}
                            >
                                ↑
                            </button>
                            <button
                                onClick={() => handleMove(col.id, 'down')}
                                disabled={index === localColumns.length - 1}
                                style={{
                                    ...styles.button,
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    opacity: index === localColumns.length - 1 ? 0.5 : 1,
                                    cursor: index === localColumns.length - 1 ? 'not-allowed' : 'pointer',
                                    backgroundColor: isDark ? '#444' : '#e0e0e0',
                                    color: styles.text
                                }}
                            >
                                ↓
                            </button>
                        </div>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button onClick={handleReset} style={{...styles.button, backgroundColor: '#6c757d', color: 'white'}}>
                        Reset to Default
                    </button>
                    <button onClick={handleSave} style={{...styles.button, backgroundColor: styles.link, color: 'white'}}>
                        Save
                    </button>
                    <button onClick={onClose} style={{...styles.button, backgroundColor: '#6c757d', color: 'white'}}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

// Get default columns based on user role
const getDefaultColumns = (isStaff) => {
    const baseColumns = [
        { id: 'aircraft', label: 'A/C', visible: true, required: true },
        { id: 'site', label: 'Site', visible: true, required: true },
        { id: 'status', label: 'Status', visible: true, required: true },
        { id: 'ak0', label: 'AK0', visible: true },
        { id: 'eddDays', label: 'EDD Days', visible: true },
        { id: 'ecdDays', label: 'ECD Days', visible: true },
        { id: 'lfdDays', label: 'LFD Days', visible: true },
        { id: 'aciDays', label: 'ACI Days', visible: true },
        { id: 'pmPercentage', label: 'PM %', visible: isStaff },
        { id: 'statusPriority', label: 'Status %', visible: isStaff },
        { id: 'ak0Priority', label: 'AK0 %', visible: isStaff },
        { id: 'eddPriority', label: 'EDD %', visible: isStaff },
        { id: 'ecdPriority', label: 'ECD %', visible: isStaff },
        { id: 'lfdPriority', label: 'LFD %', visible: isStaff },
        { id: 'aciPriority', label: 'ACI %', visible: isStaff },
        { id: 'pmPriority', label: 'PM %', visible: isStaff },
        { id: 'totalPriority', label: 'Total %', visible: true, required: true },
        { id: 'rank', label: 'Rank', visible: true, required: true },
    ];
    return baseColumns;
};


// Helper function to get priority bar color
const getPriorityBarColor = (percentage) => {
    const pct = parseFloat(percentage);
    if (pct >= 70) return '#dc3545'; // Red - High priority
    if (pct >= 50) return '#ffc107'; // Yellow - Medium priority
    if (pct >= 30) return '#17a2b8'; // Blue - Low priority
    return '#28a745'; // Green - Very low priority
};

// Helper function to get rank color
const getRankColor = (rank, totalAircraft) => {
    const rankPercent = (rank / totalAircraft) * 100;
    if (rankPercent <= 25) return '#dc3545'; // Red - Top 25%
    if (rankPercent <= 50) return '#ffc107'; // Yellow - Top 50%
    if (rankPercent <= 75) return '#17a2b8'; // Blue - Top 75%
    return '#28a745'; // Green - Bottom 25%
};

// Fallback static data for error cases
const staticAircraftData = [
    {
        buno: '168001',
        site: 'Site A',
        status: 'DN',
        ak0_count: 0,
        edd_days: 15,
        ecd_days: 5,
        lfd_days: 45,
        aci_days: 25,
        pm_percentage: 85,
        status_priority: 43.0,
        ak0_priority: 27.0,
        edd_priority: 10.0,
        ecd_priority: 2.25,
        lfd_priority: 0.5,
        aci_priority: 3.33,
        pm_priority: 4.25,
        total_priority: 90.33,
        rank: 1
    }
];

const staticSitesData = [
    { id: 1, name: 'Site A' },
    { id: 2, name: 'Site B' },
    { id: 3, name: 'Site C' }
];

function AMPT({ theme }) {
    const styles = getStyles(theme);
    const { isStaff } = useAuth();
    const [aircraftList, setAircraftList] = useState([]);
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('total_priority');
    const [sortDirection, setSortDirection] = useState('desc');
    const [statusFilter, setStatusFilter] = useState('');
    const [siteFilter, setSiteFilter] = useState('');

    const statusOptions = ['UP', 'XC', 'DN', 'DS', 'PM', 'FCF', 'AWP', 'OTH', 'DET', 'AIMD', 'ACI', 'REI'];
    const [showColumnSettings, setShowColumnSettings] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });

    // Initialize columns state with saved preferences or defaults
    const [columns, setColumns] = useState(() => {
        const saved = localStorage.getItem('ampt_columns');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                const defaults = getDefaultColumns(isStaff);
                // Merge saved columns with defaults to handle new columns or permission changes
                return defaults.map(defaultCol => {
                    const savedCol = parsed.find(c => c.id === defaultCol.id);
                    if (savedCol) {
                        // Only restore visibility if user still has permission (staff-only columns)
                        if (defaultCol.id.includes('Priority') && defaultCol.id !== 'totalPriority' && !isStaff) {
                            return { ...defaultCol, visible: false };
                        }
                        return { ...defaultCol, visible: savedCol.visible };
                    }
                    return defaultCol;
                });
            } catch (e) {
                console.error('Error loading column settings:', e);
            }
        }
        return getDefaultColumns(isStaff);
    });

    // Update columns when staff status changes
    useEffect(() => {
        const defaults = getDefaultColumns(isStaff);
        setColumns(prev => {
            return defaults.map(defaultCol => {
                const prevCol = prev.find(c => c.id === defaultCol.id);
                if (prevCol) {
                    // If column requires staff and user is no longer staff, hide it
                    if (defaultCol.id.includes('Priority') && defaultCol.id !== 'totalPriority' && !isStaff) {
                        return { ...defaultCol, visible: false };
                    }
                    return { ...defaultCol, visible: prevCol.visible };
                }
                return defaultCol;
            });
        });
    }, [isStaff]);

    // Save column settings
    const handleSaveColumnSettings = (newColumns) => {
        setColumns(newColumns);
        localStorage.setItem('ampt_columns', JSON.stringify(newColumns));
        setAlert({ show: true, message: 'Column settings saved!', type: 'success' });
        setTimeout(() => setAlert({ show: false, message: '', type: '' }), 3000);
    };

    // Helper function to check if column is visible
    const isColumnVisible = (columnId) => {
        const column = columns.find(col => col.id === columnId);
        return column ? column.visible : false;
    };

    // Fetch data from API - extracted to function for manual refresh
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Fetch AMPT configuration (currently unused but may be needed in future)
            try {
                await api.get('/ampt-configuration/');
            } catch (configErr) {
                console.warn('Could not fetch AMPT configuration, using defaults:', configErr);
                // Continue with defaults if config fetch fails
            }
            
            // Fetch maintenance priority data
            const priorityResponse = await api.get('/maintenance-priority/');
            const priorityData = priorityResponse.data.aircraft_priorities || [];
            setAircraftList(priorityData);
            
            // Fetch sites data
            const sitesResponse = await api.get('/sites/');
            const sitesData = Array.isArray(sitesResponse.data) ? sitesResponse.data : (sitesResponse.data.results || []);
            setSites(sitesData);
            
        } catch (err) {
            console.error('Error fetching maintenance priority data:', err);
            setError('Failed to load maintenance priority data. Please try again.');
            // Fallback to static data on error
            setAircraftList(staticAircraftData);
            setSites(staticSitesData);
        } finally {
            setLoading(false);
        }
    }, []); // No dependencies - fetchData doesn't depend on any props or state

    // Fetch data on component mount
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Auto-refresh when window regains focus (e.g., user switches tabs and comes back)
    useEffect(() => {
        const handleFocus = () => {
            // Refresh data when window regains focus
            fetchData();
        };

        window.addEventListener('focus', handleFocus);
        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, [fetchData]);

    // Filter aircraft data
    const filteredAircraft = aircraftList.filter(aircraft => {
        const matchesSearch = searchTerm === '' || 
            aircraft.buno.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === '' || aircraft.status === statusFilter;
        const matchesSite = siteFilter === '' || aircraft.site === siteFilter;
        return matchesSearch && matchesStatus && matchesSite;
    });

    // Recalculate ranks based on total_priority within the filtered set
    const aircraftWithRanks = [...filteredAircraft]
        .sort((a, b) => {
            // Sort by total_priority (descending) to determine ranks
            const aPriority = parseFloat(a.total_priority) || 0;
            const bPriority = parseFloat(b.total_priority) || 0;
            return bPriority - aPriority;
        })
        .map((aircraft, index) => ({
            ...aircraft,
            rank: index + 1
        }));

    // Now apply user's selected sort
    const filteredAndSortedAircraft = aircraftWithRanks.sort((a, b) => {
        if (!sortField) return 0;
        
        let aValue = a[sortField];
        let bValue = b[sortField];
        
        // Handle numeric sorting for priority fields
        if (sortField.includes('priority') || sortField === 'rank') {
            aValue = parseFloat(aValue) || 0;
            bValue = parseFloat(bValue) || 0;
        }
        
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc'); // Default to descending for priority fields
        }
    };

    // Show loading spinner while fetching data
    if (loading) {
        return (
            <div style={styles.page}>
                <div style={styles.container}>
                    <div style={styles.pageHeader}>
                        <HamburgerMenu theme={theme}/>
                        <h1 style={styles.pageTitle}>Aircraft Maintenance Priority Tool A.M.P.T</h1>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                        <Spinner />
                    </div>
                </div>
            </div>
        );
    }

    // Show error message if data fetch failed
    if (error) {
        return (
            <div style={styles.page}>
                <div style={styles.container}>
                    <div style={styles.pageHeader}>
                        <HamburgerMenu theme={theme}/>
                        <h1 style={styles.pageTitle}>Aircraft Maintenance Prioritisation Tool A.M.P.T</h1>
                    </div>
                    <div style={styles.card}>
                        <div style={{ color: '#dc3545', textAlign: 'center', padding: '20px' }}>
                            <h3>Error Loading Data</h3>
                            <p>{error}</p>
                            <button 
                                onClick={() => window.location.reload()} 
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <div style={styles.pageHeader}>
                    <HamburgerMenu theme={theme}/>
                    <h1 style={styles.pageTitle}>Aircraft Maintenance Priority Tool A.M.P.T</h1>
                    <button
                        onClick={() => setShowColumnSettings(true)}
                        style={{
                            ...styles.button,
                            backgroundColor: styles.link,
                            color: 'white',
                            padding: '8px 16px'
                        }}
                        title="Customize visible columns"
                    >
                        ⚙️ Columns
                    </button>
                </div>
                {alert.show && (
                    <div style={{
                        padding: '10px 20px',
                        marginBottom: '20px',
                        borderRadius: '4px',
                        backgroundColor: alert.type === 'success' ? '#28a745' : '#dc3545',
                        color: 'white',
                        textAlign: 'center'
                    }}>
                        {alert.message}
                    </div>
                )}

                <div style={styles.card}>
                    <h2 style={styles.cardTitle}>Priority Calculation Overview</h2>
                    <p style={{ color: styles.subtleText, marginBottom: '15px' }}>
                        This tool calculates maintenance priority based on multiple factors. Higher percentages indicate higher priority.
                        The tool helps plan maintenance by ranking aircraft based on status, AK0s, delivery dates, completion dates, 
                        last fly dates, ACI schedules, and planned maintenance percentages.
                    </p>
                </div>

                {/* Search and Filter Controls */}
                <div style={styles.controlsContainer}>
                    <input
                        type="text"
                        placeholder="Search by BUNO..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={styles.searchInput}
                    />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={styles.filterSelect}
                    >
                        <option value="">All Statuses</option>
                        {Array.isArray(statusOptions) && statusOptions.map(status => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                    </select>
                    <select
                        value={siteFilter}
                        onChange={(e) => setSiteFilter(e.target.value)}
                        style={styles.filterSelect}
                    >
                        <option value="">All Sites</option>
                        {Array.isArray(sites) && sites.map(site => (
                            <option key={site.id} value={site.name}>{site.name}</option>
                        ))}
                    </select>
                    <button
                        onClick={fetchData}
                        disabled={loading}
                        style={{
                            ...styles.filterSelect,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            backgroundColor: loading ? (theme === 'dark' ? '#555' : '#ccc') : (theme === 'dark' ? '#333' : '#fff'),
                            padding: '8px 16px',
                            fontWeight: 'bold'
                        }}
                        title="Refresh data to see latest EDD, ECD, and LFD updates"
                    >
                        {loading ? 'Refreshing...' : '🔄 Refresh'}
                    </button>
                </div>
                {alert.show && (
                    <div style={{
                        padding: '10px 20px',
                        marginBottom: '20px',
                        borderRadius: '4px',
                        backgroundColor: alert.type === 'success' ? '#28a745' : '#dc3545',
                        color: 'white',
                        textAlign: 'center'
                    }}>
                        {alert.message}
                    </div>
                )}

                <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr style={styles.tableHeader}>
                                    {isColumnVisible('aircraft') && <th style={styles.tableHeaderCell}>A/C</th>}
                                    {isColumnVisible('site') && <th style={styles.tableHeaderCell}>Site</th>}
                                    {isColumnVisible('status') && <th style={styles.tableHeaderCell}>Status</th>}
                                    {isColumnVisible('ak0') && <th style={styles.tableHeaderCell}>AK0</th>}
                                    {isColumnVisible('eddDays') && <th style={styles.tableHeaderCell}>EDD Days</th>}
                                    {isColumnVisible('ecdDays') && <th style={styles.tableHeaderCell}>ECD Days</th>}
                                    {isColumnVisible('lfdDays') && <th style={styles.tableHeaderCell}>LFD Days</th>}
                                    {isColumnVisible('aciDays') && <th style={styles.tableHeaderCell}>ACI Days</th>}
                                    {isColumnVisible('pmPercentage') && <th style={styles.tableHeaderCell}>PM %</th>}
                                    {isColumnVisible('statusPriority') && (
                                        <th style={styles.tableHeaderCell}>
                                            <div style={styles.sortableHeader} onClick={() => handleSort('status_priority')}>
                                                Status %
                                                {sortField === 'status_priority' && (
                                                    <span style={styles.sortIcon}>
                                                        {sortDirection === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    )}
                                    {isColumnVisible('ak0Priority') && (
                                        <th style={styles.tableHeaderCell}>
                                            <div style={styles.sortableHeader} onClick={() => handleSort('ak0_priority')}>
                                                AK0 %
                                                {sortField === 'ak0_priority' && (
                                                    <span style={styles.sortIcon}>
                                                        {sortDirection === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    )}
                                    {isColumnVisible('eddPriority') && (
                                        <th style={styles.tableHeaderCell}>
                                            <div style={styles.sortableHeader} onClick={() => handleSort('edd_priority')}>
                                                EDD %
                                                {sortField === 'edd_priority' && (
                                                    <span style={styles.sortIcon}>
                                                        {sortDirection === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    )}
                                    {isColumnVisible('ecdPriority') && (
                                        <th style={styles.tableHeaderCell}>
                                            <div style={styles.sortableHeader} onClick={() => handleSort('ecd_priority')}>
                                                ECD %
                                                {sortField === 'ecd_priority' && (
                                                    <span style={styles.sortIcon}>
                                                        {sortDirection === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    )}
                                    {isColumnVisible('lfdPriority') && (
                                        <th style={styles.tableHeaderCell}>
                                            <div style={styles.sortableHeader} onClick={() => handleSort('lfd_priority')}>
                                                LFD %
                                                {sortField === 'lfd_priority' && (
                                                    <span style={styles.sortIcon}>
                                                        {sortDirection === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    )}
                                    {isColumnVisible('aciPriority') && (
                                        <th style={styles.tableHeaderCell}>
                                            <div style={styles.sortableHeader} onClick={() => handleSort('aci_priority')}>
                                                ACI %
                                                {sortField === 'aci_priority' && (
                                                    <span style={styles.sortIcon}>
                                                        {sortDirection === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    )}
                                    {isColumnVisible('pmPriority') && (
                                        <th style={styles.tableHeaderCell}>
                                            <div style={styles.sortableHeader} onClick={() => handleSort('pm_priority')}>
                                                PM %
                                                {sortField === 'pm_priority' && (
                                                    <span style={styles.sortIcon}>
                                                        {sortDirection === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    )}
                                    {isColumnVisible('totalPriority') && (
                                        <th style={styles.tableHeaderCell}>
                                            <div style={styles.sortableHeader} onClick={() => handleSort('total_priority')}>
                                                Total %
                                                {sortField === 'total_priority' && (
                                                    <span style={styles.sortIcon}>
                                                        {sortDirection === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    )}
                                    {isColumnVisible('rank') && (
                                        <th style={styles.tableHeaderCell}>
                                            <div style={styles.sortableHeader} onClick={() => handleSort('rank')}>
                                                Rank
                                                {sortField === 'rank' && (
                                                    <span style={styles.sortIcon}>
                                                        {sortDirection === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {Array.isArray(filteredAndSortedAircraft) && filteredAndSortedAircraft.map(ac => (
                                    <tr key={ac.buno} style={styles.tableRow}>
                                        {isColumnVisible('aircraft') && (
                                            <td style={styles.tableCell}>
                                                <span style={{ fontWeight: 'bold' }}>{ac.buno}</span>
                                            </td>
                                        )}
                                        {isColumnVisible('site') && (
                                            <td style={styles.tableCell}>
                                                <span style={{
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    fontSize: '10px',
                                                    fontWeight: 'bold',
                                                    backgroundColor: styles.link,
                                                    color: 'white'
                                                }}>
                                                    {ac.site || 'N/A'}
                                                </span>
                                            </td>
                                        )}
                                        {isColumnVisible('status') && (
                                            <td style={styles.tableCell}>
                                                <span style={{
                                                    ...styles.statusBadge, 
                                                    backgroundColor: (ac.status && styles.status[ac.status]) || '#6c757d'
                                                }}>
                                                    {ac.status || 'N/A'}
                                                </span>
                                            </td>
                                        )}
                                        {isColumnVisible('ak0') && <td style={styles.tableCell}>{ac.ak0_count || 0}</td>}
                                        {isColumnVisible('eddDays') && <td style={styles.tableCell}>{ac.edd_days || 'N/A'}</td>}
                                        {isColumnVisible('ecdDays') && <td style={styles.tableCell}>{ac.ecd_days || 'N/A'}</td>}
                                        {isColumnVisible('lfdDays') && <td style={styles.tableCell}>{ac.lfd_days || 'N/A'}</td>}
                                        {isColumnVisible('aciDays') && <td style={styles.tableCell}>{ac.aci_days || 'N/A'}</td>}
                                        {isColumnVisible('pmPercentage') && <td style={styles.tableCell}>{ac.pm_percentage || 0}%</td>}
                                        {isColumnVisible('statusPriority') && <td style={styles.tableCell}>{ac.status_priority || 0}%</td>}
                                        {isColumnVisible('ak0Priority') && <td style={styles.tableCell}>{ac.ak0_priority || 0}%</td>}
                                        {isColumnVisible('eddPriority') && <td style={styles.tableCell}>{ac.edd_priority || 0}%</td>}
                                        {isColumnVisible('ecdPriority') && <td style={styles.tableCell}>{ac.ecd_priority || 0}%</td>}
                                        {isColumnVisible('lfdPriority') && <td style={styles.tableCell}>{ac.lfd_priority || 0}%</td>}
                                        {isColumnVisible('aciPriority') && <td style={styles.tableCell}>{ac.aci_priority || 0}%</td>}
                                        {isColumnVisible('pmPriority') && <td style={styles.tableCell}>{ac.pm_priority || 0}%</td>}
                                        {isColumnVisible('totalPriority') && (
                                            <td style={styles.tableCell}>
                                                <div style={{
                                                    ...styles.priorityBar,
                                                    backgroundColor: getPriorityBarColor(ac.total_priority),
                                                    width: `${Math.min(parseFloat(ac.total_priority), 100)}%`
                                                }}>
                                                    {ac.total_priority || 0}%
                                                </div>
                                            </td>
                                        )}
                                        {isColumnVisible('rank') && (
                                            <td style={{
                                                ...styles.tableCell,
                                                ...styles.rankCell,
                                                backgroundColor: getRankColor(ac.rank, filteredAndSortedAircraft.length)
                                            }}>
                                                {ac.rank}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                </div>

                {showColumnSettings && (
                    <ColumnSettingsModal
                        columns={columns}
                        onClose={() => setShowColumnSettings(false)}
                        onSave={handleSaveColumnSettings}
                        theme={theme}
                        isStaff={isStaff}
                    />
                )}

            </div>
        </div>
    );
}

export default AMPT;
